import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft, FileText, Upload, Trash2, Download, Loader2,
  AlertCircle, CheckCircle, File, RefreshCw, Info
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { estafasBancariasAPI } from '../api';
import { toast } from '../hooks/use-toast';

const TIPOS_PLANTILLA = [
  { id: 'reclamacion', codigo: 'D', nombre: 'Reclamación Extrajudicial',
    descripcion: 'Documento de reclamación formal al banco (Documento D)',
    icon: FileText, color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { id: 'hoja_encargo', codigo: 'E', nombre: 'Hoja de Encargo',
    descripcion: 'Documento de encargo profesional del cliente (Documento E)',
    icon: FileText, color: 'bg-green-100 text-green-700 border-green-200' },
  { id: 'informe', codigo: 'F', nombre: 'Informe Resumen',
    descripcion: 'Informe resumen del caso de estafa (Documento F)',
    icon: FileText, color: 'bg-purple-100 text-purple-700 border-purple-200' }
];

const ConfiguracionPlantillasEstafas = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [plantillas, setPlantillas] = useState([]);
  const [uploading, setUploading] = useState({});
  const [deleteDialog, setDeleteDialog] = useState({ open: false, plantilla: null });
  const fileInputRefs = useRef({});

  useEffect(() => { loadPlantillas(); }, []);

  const loadPlantillas = async () => {
    setLoading(true);
    try {
      const response = await estafasBancariasAPI.getPlantillas();
      setPlantillas(response.data || []);
    } catch (error) {
      console.error('Error cargando plantillas:', error);
      toast({ title: "Error", description: "No se pudieron cargar las plantillas", variant: "destructive" });
    } finally { setLoading(false); }
  };

  const handleFileSelect = async (tipo, event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const validTypes = ['application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword', 'application/vnd.oasis.opendocument.text'];
    if (!validTypes.includes(file.type) && !file.name.match(/\.(docx?|odt)$/i)) {
      toast({ title: "Tipo de archivo no válido",
        description: "Por favor, sube un archivo Word (.doc, .docx) o OpenDocument (.odt)", variant: "destructive" });
      return;
    }
    setUploading(prev => ({ ...prev, [tipo]: true }));
    try {
      const formData = new FormData();
      formData.append('archivo', file);
      formData.append('tipo', tipo);
      formData.append('nombre', file.name);
      await estafasBancariasAPI.uploadPlantilla(formData);
      toast({ title: "Plantilla subida", description: `La plantilla "${file.name}" se ha guardado correctamente` });
      await loadPlantillas();
    } catch (error) {
      toast({ title: "Error al subir", description: error.response?.data?.detail || "No se pudo subir la plantilla", variant: "destructive" });
    } finally {
      setUploading(prev => ({ ...prev, [tipo]: false }));
      if (fileInputRefs.current[tipo]) { fileInputRefs.current[tipo].value = ''; }
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.plantilla) return;
    try {
      await estafasBancariasAPI.deletePlantilla(deleteDialog.plantilla.id);
      toast({ title: "Plantilla eliminada", description: "La plantilla se ha eliminado correctamente" });
      await loadPlantillas();
    } catch (error) {
      toast({ title: "Error", description: "No se pudo eliminar la plantilla", variant: "destructive" });
    } finally { setDeleteDialog({ open: false, plantilla: null }); }
  };

  const getPlantillaByTipo = (tipo) => plantillas.find(p => p.tipo === tipo);
  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (<div className="p-6 flex items-center justify-center min-h-[400px]">
      <div className="flex items-center gap-3"><Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      <span className="text-gray-600">Cargando plantillas...</span></div></div>);
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/configuracion')}><ChevronLeft className="w-4 h-4 mr-2" />Volver</Button>
        <div className="flex-1"><h1 className="text-2xl font-bold text-gray-900">Plantillas de Estafas Bancarias</h1>
        <p className="text-gray-600">Gestiona las plantillas Word para generar documentos del módulo de estafas</p></div>
        <Button variant="outline" onClick={loadPlantillas}><RefreshCw className="w-4 h-4 mr-2" />Actualizar</Button>
      </div>
      <Card className="bg-blue-50 border-blue-200"><CardContent className="p-4"><div className="flex gap-3">
        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800"><p className="font-medium mb-1">Información sobre las plantillas</p>
        <p>Sube plantillas Word (.docx) con marcadores como <code className="bg-blue-100 px-1 rounded">{'{{cliente_nombre}}'}</code>, <code className="bg-blue-100 px-1 rounded">{'{{banco_nombre}}'}</code>, <code className="bg-blue-100 px-1 rounded">{'{{importe}}'}</code></p>
        </div></div></CardContent></Card>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {TIPOS_PLANTILLA.map((tipo) => {
          const plantilla = getPlantillaByTipo(tipo.id);
          const isUploading = uploading[tipo.id];
          const Icon = tipo.icon;
          return (
            <Card key={tipo.id} className={`relative ${plantilla ? 'border-green-200 bg-green-50/30' : 'border-dashed border-2 border-gray-300'}`}>
              <CardHeader className="pb-3"><div className="flex items-start justify-between"><div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tipo.color}`}><Icon className="w-5 h-5" /></div>
                <div><CardTitle className="text-base flex items-center gap-2">{tipo.nombre}<Badge variant="outline" className="text-xs">Doc. {tipo.codigo}</Badge></CardTitle>
                <CardDescription className="text-xs mt-1">{tipo.descripcion}</CardDescription></div></div></div></CardHeader>
              <CardContent className="pt-0">
                {plantilla ? (<div className="space-y-3">
                  <div className="p-3 bg-white rounded-lg border border-gray-200">
                    <div className="flex items-center gap-2 mb-2"><File className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium text-gray-900 truncate flex-1">{plantilla.nombre_archivo || plantilla.nombre}</span></div>
                    <div className="flex items-center gap-4 text-xs text-gray-500"><span>{formatFileSize(plantilla.tamano)}</span><span>•</span><span>{formatDate(plantilla.updated_at || plantilla.created_at)}</span></div></div>
                  <div className="flex items-center gap-2 text-sm text-green-700"><CheckCircle className="w-4 h-4" /><span>Plantilla configurada</span></div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => fileInputRefs.current[tipo.id]?.click()} disabled={isUploading}>
                      {isUploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}Reemplazar</Button>
                    <Button variant="outline" size="sm" onClick={() => setDeleteDialog({ open: true, plantilla })} className="text-red-600 hover:text-red-700 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button></div>
                </div>) : (<div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-amber-600"><AlertCircle className="w-4 h-4" /><span>Sin plantilla configurada</span></div>
                  <Button variant="outline" className="w-full" onClick={() => fileInputRefs.current[tipo.id]?.click()} disabled={isUploading}>
                    {isUploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}Subir plantilla Word</Button>
                </div>)}
                <input type="file" ref={(el) => (fileInputRefs.current[tipo.id] = el)} className="hidden" accept=".doc,.docx,.odt" onChange={(e) => handleFileSelect(tipo.id, e)} />
              </CardContent></Card>);
        })}
      </div>
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, plantilla: null })}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>¿Eliminar plantilla?</AlertDialogTitle>
        <AlertDialogDescription>Esta acción eliminará la plantilla. Los documentos ya generados no se verán afectados.</AlertDialogDescription></AlertDialogHeader>
        <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel>
        <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction></AlertDialogFooter></AlertDialogContent></AlertDialog>
    </div>);
};

export default ConfiguracionPlantillasEstafas;
